import React, { useState, useEffect, useRef, useCallback } from 'react';
import api from '../../lib/api';
import {
  ListOrdered, Loader2, ArrowUp, ArrowDown, Minus, Trophy,
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

const fitColor = (fit) =>
  fit >= 70 ? 'text-emerald-600 bg-emerald-500/10 border-emerald-500/30'
  : fit >= 45 ? 'text-amber-600 bg-amber-500/10 border-amber-500/30'
  : 'text-muted-foreground bg-muted border-border';

const Movement = ({ m }) => {
  if (m > 0) return (
    <span className="inline-flex items-center gap-0.5 text-[11px] font-semibold text-emerald-600" data-testid="icp-lead-movement-up">
      <ArrowUp className="w-3 h-3" />{m}
    </span>
  );
  if (m < 0) return (
    <span className="inline-flex items-center gap-0.5 text-[11px] font-semibold text-red-500" data-testid="icp-lead-movement-down">
      <ArrowDown className="w-3 h-3" />{Math.abs(m)}
    </span>
  );
  return (
    <span className="inline-flex items-center text-[11px] text-muted-foreground" data-testid="icp-lead-movement-none">
      <Minus className="w-3 h-3" />
    </span>
  );
};

// Live, no-save preview that re-ranks the tenant's pipeline leads under the
// weights currently being tuned, so "fit" changes visibly reorder the Brain's
// top prospects. Debounced; platform-paid ($0, pure math).
export const ICPTopLeadsPreview = ({ weights, editing }) => {
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [empty, setEmpty] = useState(false);
  const debounceRef = useRef(null);

  const fetchPreview = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.post(`${API}/api/brain/icp/preview-top-leads`, {
        weights: weights || {}, limit: 10,
      });
      const leads = r.data?.leads || [];
      setRows(leads);
      setTotal(r.data?.total_ranked || 0);
      setEmpty(leads.length === 0);
    } catch {
      setRows([]); setEmpty(true);
    } finally { setLoading(false); }
  }, [weights]);

  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => { fetchPreview(); }, 450);
    return () => debounceRef.current && clearTimeout(debounceRef.current);
  }, [fetchPreview]);

  return (
    <div className="rounded-xl border bg-card p-5 shadow-sm" data-testid="icp-top-leads-preview">
      <div className="flex items-center justify-between mb-3 gap-3 flex-wrap">
        <div>
          <h3 className="font-semibold text-foreground flex items-center gap-2">
            <ListOrdered className="w-4 h-4 text-primary" /> Top Leads For These Weights
          </h3>
          <p className="text-xs text-muted-foreground mt-0.5">
            {editing
              ? 'Live preview — tune the weights above and watch the ranking reorder.'
              : 'Who the Brain prioritises under your saved weights.'}
            {total > 0 && <span className="ml-1">Ranked {total.toLocaleString()} pipeline leads.</span>}
          </p>
        </div>
        {loading && (
          <span className="inline-flex items-center gap-1.5 text-xs text-muted-foreground" data-testid="icp-top-leads-loading">
            <Loader2 className="w-3.5 h-3.5 animate-spin" /> Re-ranking…
          </span>
        )}
      </div>

      {empty && !loading ? (
        <p className="text-sm text-muted-foreground italic py-6 text-center" data-testid="icp-top-leads-empty">
          No scored pipeline leads yet — discover and import leads to see them ranked here.
        </p>
      ) : (
        <div className="space-y-1.5" data-testid="icp-top-leads-list">
          {rows.map((l, i) => (
            <div key={l.id || i}
              className="flex items-center gap-3 rounded-lg border border-border bg-background/60 px-3 py-2 transition-colors hover:bg-muted/40"
              data-testid={`icp-top-lead-row-${i}`}>
              <div className="flex items-center justify-center w-6 h-6 rounded-full bg-primary/10 text-primary text-xs font-bold shrink-0">
                {i === 0 ? <Trophy className="w-3.5 h-3.5" /> : i + 1}
              </div>
              <div className="min-w-0 flex-1">
                <p className="text-sm font-medium text-foreground truncate" data-testid={`icp-top-lead-name-${i}`}>
                  {l.name}
                </p>
                <p className="text-xs text-muted-foreground truncate">
                  {[l.title, l.company].filter(Boolean).join(' · ')}
                </p>
              </div>
              <span className="text-[10px] px-1.5 py-0.5 rounded-full border border-border bg-muted/50 text-muted-foreground shrink-0 hidden sm:inline"
                data-testid={`icp-top-lead-stage-${i}`}>
                {l.pipeline_stage}
              </span>
              <Movement m={l.movement} />
              <span className={`text-sm font-bold px-2 py-0.5 rounded-lg border shrink-0 ${fitColor(l.fit)}`}
                data-testid={`icp-top-lead-fit-${i}`}>
                {l.fit}
              </span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default ICPTopLeadsPreview;
